import React, { useEffect, useState } from 'react';
import { CheckCircle, Sparkles, ArrowRight } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';

export default function OnboardingSuccess({ accountCount, onContinue }) {
  const { theme } = useTheme();
  const [countdown, setCountdown] = useState(3);

  useEffect(() => {
    const timer = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          if (onContinue) {
            onContinue();
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [onContinue]);

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: theme.colors.bg.primary,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '2rem',
      zIndex: 9999
    }}>
      <div style={{ 
        maxWidth: '600px', 
        width: '100%',
        textAlign: 'center'
      }}>
        {/* Progress Indicator */}
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          gap: '12px',
          marginBottom: '3rem'
        }}>
          {[1, 2, 3].map((step) => (
            <React.Fragment key={step}>
              <div style={{
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                background: theme.colors.status.success,
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: '700',
                fontSize: '1.1rem'
              }}>
                ✓
              </div>
              {step < 3 && (
                <div style={{
                  width: '40px',
                  height: '2px',
                  background: theme.colors.status.success,
                  alignSelf: 'center'
                }} />
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Success Icon */}
        <div style={{
          width: '120px',
          height: '120px',
          margin: '0 auto 2rem',
          background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 10px 40px rgba(16, 185, 129, 0.4)',
          animation: 'scaleIn 0.5s ease-out'
        }}>
          <CheckCircle size={64} color="white" strokeWidth={3} />
        </div>

        {/* Heading */}
        <h1 style={{
          fontSize: '2.5rem',
          fontWeight: '700',
          color: theme.colors.text.primary,
          marginBottom: '1rem',
          lineHeight: '1.2'
        }}>
          All Set! 🎉
        </h1>

        {/* Subtitle */}
        <p style={{
          fontSize: '1.2rem',
          color: theme.colors.text.secondary,
          marginBottom: '2rem',
          lineHeight: '1.6'
        }}>
          Successfully connected {accountCount} account{accountCount !== 1 ? 's' : ''}.<br />
          Redirecting to your dashboard...
        </p>

        {/* Countdown */}
        <div style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '12px',
          padding: '1rem 2rem',
          background: theme.colors.bg.secondary,
          border: `1px solid ${theme.colors.border.primary}`,
          borderRadius: '12px',
          marginBottom: '2rem'
        }}>
          <Sparkles size={24} color={theme.colors.brand.primary} />
          <span style={{
            fontSize: '1rem',
            color: theme.colors.text.primary,
            fontWeight: '600'
          }}>
            Starting in {countdown} second{countdown !== 1 ? 's' : ''}...
          </span>
        </div>

        {/* Manual Continue Button */}
        <button
          onClick={onContinue}
          style={{
            padding: '0.75rem 1.5rem',
            background: 'transparent',
            color: theme.colors.brand.primary,
            border: `2px solid ${theme.colors.brand.primary}`,
            borderRadius: '12px',
            fontSize: '1rem',
            fontWeight: '600',
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            transition: 'all 0.3s'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = theme.colors.brand.primary;
            e.currentTarget.style.color = 'white';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'transparent';
            e.currentTarget.style.color = theme.colors.brand.primary;
          }}
        >
          Skip Wait - Go Now
          <ArrowRight size={20} />
        </button>
      </div>

      <style>{`
        @keyframes scaleIn {
          from {
            transform: scale(0);
            opacity: 0;
          }
          to {
            transform: scale(1);
            opacity: 1;
          }
        }
      `}</style>
    </div>
  );
}

